import { css } from '@emotion/react';
import { theme } from '@frontend/theme';

type TabBarStyleProps = { fullWidth?: boolean; isScrollable?: boolean };

export const tabBarStyle = ({ fullWidth, isScrollable }: TabBarStyleProps) => css`
  width: 100%;
  min-height: 40px;
  height: 40px;
  max-width: 100%;
  display: flex;
  overflow: auto hidden;
  position: relative;
  -ms-overflow-style: none;
  scrollbar-width: none;
  scroll-behavior: smooth;
  :focus {
    outline: none;
  }

  ${!isScrollable &&
  `::after {
    content: '';
    position: absolute;
    bottom: 0px;
    border-bottom: 1px solid ${theme.colors.neutral20};
    pointer-events: none;
    left: 0;
    width: 100%;
  }`}

  ::-webkit-scrollbar {
    display: none;
  }

  .sliding__border {
    border-bottom: 2px solid ${theme.colors.primary50};
    position: absolute;
    bottom: ${isScrollable ? '-1' : '0'}px;
    z-index: 1;
    width: 100%;
  }

  a,
  button {
    ${fullWidth && `flex: 1 0 auto;`}
    padding: 0 ${theme.spacing(2)};
    flex-shrink: 0;
    ${isScrollable && `border-bottom: 1px solid ${theme.colors.neutral20};`}
  }
`;

export const tabItemStyle = css`
  font-family: ${theme.font.family};
  font-size: ${theme.font.size.medium};
  color: ${theme.font.colors.default};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-items: center;
  position: relative;
  text-decoration: none;
  transition: background 0.25s ease-out;

  :before {
    content: attr(data-text);
    content: attr(data-text) / '';
    height: 0;
    visibility: hidden;
    overflow: hidden;
    user-select: none;
    pointer-events: none;
    font-family: ${theme.font.family};
    font-size: ${theme.font.size.medium};
    font-weight: ${theme.font.weight.bold};
    @media speech {
      display: none;
    }
  }

  :hover:not(:disabled),
  :focus {
    background: ${theme.colors.neutral5};
    color: ${theme.colors.neutral80};
    outline: none;
  }

  &.selected,
  &[aria-selected='true'] {
    font-weight: ${theme.font.weight.bold};
    color: ${theme.colors.primary50};
    :focus::after {
      background: ${theme.colors.neutral80};
    }
  }
  :disabled {
    color: ${theme.font.colors.disabled};
  }
`;

type ScrollArrowStyleProps = { disabled: boolean; direction: 'left' | 'right' };

export const scrollArrowStyle = ({ disabled, direction }: ScrollArrowStyleProps) => {
  return css`
    position: relative;
    ::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: -1px;
      width: 32px;
      transition: opacity 0.3s;
      pointer-events: none;
      opacity: 0;
      ${direction === 'left'
        ? `
        right: 0;
        transform: translateX(100%);
        box-shadow: ${theme.shadows.innerLeft};`
        : `
        left: 0;
        transform: translateX(-100%);
        box-shadow: ${theme.shadows.innerRight};
        `}
    }

    ${!disabled &&
    `::after {
        opacity: 1;
    }`}

    button {
      ${scrollingArrowsDefaultStyle}
      padding: 0 ${theme.spacing(0.5)};
    }

    svg {
      ${disabled && `color: ${theme.colors.neutral20};`}
      transition: 0.2s ease;
    }
  `;
};

const scrollingArrowsDefaultStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

export const TabsStyles = {
  tabItem: tabItemStyle,
  tabBar: (tabBarProps: TabBarStyleProps) => tabBarStyle(tabBarProps),
  scrollArrowStyle: (scrollArrowProps: ScrollArrowStyleProps) => scrollArrowStyle(scrollArrowProps),
};
